import React from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { useStyles } from './style';

// Define the interface for the business card data
interface BusinessCard {
    id?: string;
    businessImage: string;
    logo: string;
    category: string;
    name: string;
    location: string;
    contactName: string;
    contactImage: string;
}

interface BusinessCardContainerProps {
    cardData: BusinessCard[];
    onCardClick: (businessId: string) => void;
}

const BusinessCardContainer: React.FC<BusinessCardContainerProps> = (props) => {
    const cardData = React.useMemo(
        () =>
            props.cardData.map((card, index) => ({
                ...card,
                id: card.id || `business-${index}`,
            })),
        [props.cardData]
    );

    const classes = useStyles();

    // Handler for clicking on a business card
    const handleCardClick = (businessId: string) => {
        props.onCardClick(businessId);
    };

    return (
        <Box>
            <Grid container spacing={2}>
                {cardData.map((item, index) => (
                    <Grid item md={4} lg={4} xl={4} sm={6} xs={12} key={index} p={1}>
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                overflow: 'hidden',
                                backgroundColor: '#F9FAFB',
                                cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                minWidth: { xs: '100%', sm: '100%' },
                                height: '100%',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                }
                            }}
                            onClick={() => handleCardClick(item.id!)}
                        >
                            <Box sx={{ position: 'relative', height: 180 }}>
                                <img
                                    src={item.businessImage}
                                    alt="Business Background"
                                    style={{ width: '100%', height: '160px', objectFit: 'cover' }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 12,
                                        right: 12,
                                        backgroundColor: '#fff',
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: '16px',
                                        fontSize: 12,
                                        fontWeight: 500,
                                        color: '#333',
                                    }}
                                >
                                    {item.category}
                                </Box>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: -6,
                                        left: '12%',
                                        transform: 'translateX(-50%)',
                                        zIndex: 1,
                                        width: 48,
                                        height: 48,
                                        borderRadius: '50%',
                                        overflow: 'hidden',
                                        border: '2px solid white',
                                        backgroundColor: '#fff',
                                    }}
                                >
                                    <img
                                        src={item.logo}
                                        alt="Logo"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                </Box>
                            </Box>
                            <Box sx={{ pt: 2, pb: 2, px: 3, textAlign: "left" }}>
                                <Typography fontWeight={600} fontSize={"15px"} color={"#373A45"}>
                                    {item.name}
                                </Typography>
                                <Box display="flex" alignItems="center" mt={1} mb={1}>
                                    <LocationOnOutlinedIcon fontSize="small" sx={{ color: '#666', mr: 0.5 }} />
                                    <Typography fontWeight={400} fontSize={"14px"} color={"#373A45"}>
                                        {item.location}
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" mt={2}>
                                    <img
                                        src={item.contactImage}
                                        alt="User"
                                        style={{ width: 28, height: 28, borderRadius: '50%', marginRight: 8 }}
                                    />
                                    <Typography fontWeight={400} fontSize={"14px"} color={"#373A45"}>
                                        {item.contactName}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default BusinessCardContainer;
